/* CCTV & Monitoring Centre — hero CTA that sits next to the AI Deep
   Research button on the dashboard. Opens a dialog that hosts the
   "Integrate your CCTV system" action. The integration form captures
   the details of a camera system / NVR feed (name, location, RTSP or
   stream URL) so an operator can register the feeds they want surfaced
   in the monitoring centre.

   NOTE: this is front-end scaffolding for the monitoring centre. Live
   video streaming from a specific VMS/NVR (e.g. RTSP, Verkada,
   Milestone) is a separate integration and is intentionally not wired
   to a backend yet — registered feeds are kept locally so the UI is
   usable while that integration is chosen. */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Cctv, Plus, Video, MapPin, Link2, Trash2, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

const STORAGE_KEY = 'atrium_cctv_feeds';

function loadFeeds() {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

export default function CCTVMonitoringButton() {
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [feeds, setFeeds] = useState([]);
  const [form, setForm] = useState({ name: '', location: '', url: '' });

  useEffect(() => {
    if (open) {
      setFeeds(loadFeeds());
      setShowForm(false);
    }
  }, [open]);

  const persist = useCallback((next) => {
    setFeeds(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota errors */
    }
  }, []);

  const addFeed = useCallback(() => {
    const name = form.name.trim();
    if (!name) {
      toast.error('Give the camera / system a name.');
      return;
    }
    const feed = {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()),
      name,
      location: form.location.trim(),
      url: form.url.trim(),
      created_at: new Date().toISOString(),
    };
    persist([feed, ...feeds]);
    setForm({ name: '', location: '', url: '' });
    setShowForm(false);
    toast.success('CCTV feed registered in the Monitoring Centre.');
  }, [form, feeds, persist]);

  const removeFeed = useCallback((id) => {
    persist(feeds.filter((f) => f.id !== id));
  }, [feeds, persist]);

  return (
    <>
      <button
        type="button"
        data-testid="hero-cctv-cta"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5"
        style={{
          fontFamily: 'Manrope, sans-serif',
          background: 'linear-gradient(135deg, #0F766E 0%, #115E59 55%, #0A1A2F 100%)',
          border: '1px solid rgba(163,212,212,0.35)',
        }}
        title="Open the CCTV & Monitoring Centre"
      >
        <Cctv className="w-4 h-4" />
        CCTV &amp; Monitoring Centre
      </button>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="cctv-dialog">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <ShieldCheck className="w-5 h-5 text-teal-700" />
            CCTV &amp; Monitoring Centre
          </DialogTitle>
          <DialogDescription>
            Register and manage the camera systems and video feeds you want to monitor across the property.
          </DialogDescription>

          {!showForm && (
            <button
              type="button"
              data-testid="cctv-integrate-btn"
              onClick={() => setShowForm(true)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #0F766E 0%, #115E59 100%)' }}
            >
              <Plus className="w-4 h-4" />
              Integrate your CCTV system
            </button>
          )}

          {showForm && (
            <div className="mt-4 space-y-3 rounded-xl border border-gray-200 p-4 bg-gray-50" data-testid="cctv-integrate-form">
              <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <Video className="w-4 h-4 text-teal-700" /> Integrate your CCTV system
              </p>
              <div>
                <label className="text-xs font-semibold text-gray-600">Camera / system name</label>
                <input
                  data-testid="cctv-name-input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Lobby NVR — Channel 1"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</label>
                <input
                  data-testid="cctv-location-input"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. Main Lobby"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1"><Link2 className="w-3 h-3" /> Stream / RTSP URL <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  data-testid="cctv-url-input"
                  value={form.url}
                  onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  placeholder="rtsp://…  or  https://…"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  data-testid="cctv-save-btn"
                  onClick={addFeed}
                  className="flex-1 rounded-lg bg-teal-700 text-white font-bold text-sm py-2.5 hover:bg-teal-800 transition-colors"
                >
                  Connect feed
                </button>
                <button
                  type="button"
                  data-testid="cctv-cancel-btn"
                  onClick={() => setShowForm(false)}
                  className="px-4 rounded-lg border border-gray-300 text-gray-600 font-semibold text-sm py-2.5 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Registered feeds */}
          {feeds.length > 0 ? (
            <div className="mt-5" data-testid="cctv-feed-list">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                Registered feeds ({feeds.length})
              </p>
              <ul className="space-y-2">
                {feeds.map((f) => (
                  <li key={f.id} data-testid={`cctv-feed-${f.id}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200">
                    <div className="w-8 h-8 rounded-md bg-teal-50 flex items-center justify-center shrink-0">
                      <Video className="w-4 h-4 text-teal-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{f.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {f.location || 'No location'}{f.url ? ` · ${f.url}` : ''}
                      </p>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700 shrink-0">Pending connection</span>
                    <button type="button" onClick={() => removeFeed(f.id)} className="p-1 text-gray-400 hover:text-red-600" title="Remove" data-testid={`cctv-remove-${f.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            !showForm && (
              <div className="mt-5 text-center text-gray-400 py-6" data-testid="cctv-empty">
                <Cctv className="w-9 h-9 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No camera systems connected yet.</p>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
